// This file is a copy of the Drag and Drop component from the Atlas Lens design system.
// Once migrated to the Atlas Lens design system, this file should be removed.
// The component should be imported from the Atlas Lens design system.
// eslint-disable-next-line max-len
// Libraries to be removed after component migration: @diligentcorp/atlas-react-utils, @hello-pangea/dnd

import { applyTokens } from '@diligentcorp/atlas-react-utils/lib';
import {
  DragDropContext,
  Draggable,
  DraggableProps,
  Droppable,
  type DraggingStyle,
  type DragStart,
  type DragUpdate,
  type DropResult,
  type NotDraggingStyle,
  type ResponderProvided,
} from '@hello-pangea/dnd';
import { useAtlasTheme } from '@issuemanager/apps/client/theme';
import { Box, useEventCallback } from '@mui/material';
import { isEmpty } from 'lodash';
import React, { useState } from 'react';

// #region Types

export interface DragAndDropItem<T>
  extends Pick<DraggableProps, 'isDragDisabled'> {
  /**
   * The unique identifier of the item.
   * This is used to identify the item in the drag and drop component.
   * It is important that this value is unique.
   * If the items are not unique, the drag and drop component will not work as expected.
   */
  id: string;

  /**
   * The content of the item can be of any type.
   * This allows us to have flexibility in the content that is displayed and be able
   * to handle it from outside the component.
   */
  content: React.ReactNode;

  /**
   * The data that is associated with the item.
   */
  data?: T;
}

export type DragAndDropProps<T> = {
  /**
   * The items that will be displayed in the drag and drop component
   */
  items: DragAndDropItem<T>[];

  /**
   * This function receives the new order of the items in the first parameter
   * and the result of the drag and drop if you want to have more control over
   * the event.
   *
   * @param orderedItems - The items in the new order
   * @param result - The result of the drag and drop
   */
  onDragEnd?: (orderedItems: DragAndDropItem<T>[], result: DropResult) => void;

  /**
   * This function is called when the drag starts.
   *
   * @param event - The drag start event
   * @param provider - The responder provided
   */
  onDragStart?: (event: DragStart, provider: ResponderProvided) => void;

  /**
   * Indicates if the drag and drop component is disabled.
   * This will prevent the user from dragging the items and also hides the
   * drag and drop icon.
   */
  isDisabled?: boolean;
};

export type DragAndDropPlaceholderType = {
  clientHeight: number;
  clientWidth: number;
  clientY: number;
  clientX: number;
};

// #endregion

// #region Utils

/**
 * Attribute that @@hello-pangea/dnd adds to the elements.
 * We need this to query the dragged element
 */
const queryAttr = 'data-rfd-drag-handle-draggable-id';

/**
 * Get the the element that is being dragged
 * @param draggableId - The id of the draggable element
 * @returns The dragged element
 */
function getDraggedDOM(draggableId: string) {
  const domQuery = `[${queryAttr}='${draggableId}']`;

  return document.querySelector(domQuery);
}

/**
 * Calculate the position of the placeholder.
 * The @@hello-pangea/dnd library does not provide a solution for styling the
 * placeholder, so i had to calculate the position of the placeholder manually.
 *
 * @param draggedDOM - The dragged element
 * @param array - The array of elements
 * @param index - The index of the dragged element
 */
function calculatePlaceholderPosition(
  draggedDOM: Element,
  array: Element[],
  index: number,
) {
  const { clientHeight, clientWidth } = draggedDOM;

  const parent = draggedDOM.parentNode as HTMLElement;

  const clientY =
    parseFloat(window.getComputedStyle(parent).paddingTop) +
    array.slice(0, index).reduce((total, curr) => {
      const style = window.getComputedStyle(curr);

      const paddingVertical =
        parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);

      const marginVertical =
        parseFloat(style.marginTop) + parseFloat(style.marginBottom);

      return total + curr.clientHeight + paddingVertical + marginVertical;
    }, 0);

  return {
    clientHeight,
    clientWidth,
    clientY,
    clientX: parseFloat(window.getComputedStyle(parent).paddingLeft),
  };
}

// #endregion

/**
 * Drag and Drop component
 */
export function DragAndDrop<T>({
  items,
  isDisabled,
  onDragEnd,
  onDragStart,
}: DragAndDropProps<T>): React.JSX.Element {
  const [placeholderProps, setPlaceholderProps] =
    useState<DragAndDropPlaceholderType>();

  const {
    tokens: {
      component: { dragAndDrop: dragAndDropTokens },
    },
  } = useAtlasTheme();

  /**
   * Handle the drag start event.
   * This is used to calculate the position of the placeholder.
   *
   * @param event - The drag start event
   */
  const handleDragStart = useEventCallback(
    (event: DragStart, provider: ResponderProvided) => {
      const draggedDOM = getDraggedDOM(event.draggableId);

      if (!draggedDOM) {
        return;
      }

      if (!draggedDOM.parentNode) {
        return;
      }

      const sourceIndex = event.source.index;

      const placeholderProps = calculatePlaceholderPosition(
        draggedDOM,
        Array.from(draggedDOM.parentNode.children),
        sourceIndex,
      );
      setPlaceholderProps(placeholderProps);

      if (onDragStart) onDragStart(event, provider);
    },
  );

  /**
   * Handle the drag update event.
   * This is used to calculate the position of the placeholder.
   *
   * @param event - The drag update event
   */
  const handleDragUpdate = useEventCallback((event: DragUpdate) => {
    if (!event.destination) {
      setPlaceholderProps(undefined);
      return;
    }

    const draggedDOM = getDraggedDOM(event.draggableId);

    if (!draggedDOM) {
      return;
    }

    if (!draggedDOM.parentNode) {
      return;
    }

    const sourceIndex = event.source.index;
    const destinationIndex = event.destination.index;

    // Check if the destination is before a non-draggable item
    const isBeforeNonDraggable = items
      .slice(destinationIndex)
      .some((item) => item.isDragDisabled);

    // If dropping before a non-draggable item, hide the placeholder
    if (isBeforeNonDraggable) {
      setPlaceholderProps(undefined);
      return;
    }

    const childrenArray = Array.from(draggedDOM.parentNode.children);
    const movedItem = childrenArray[sourceIndex];

    childrenArray.splice(sourceIndex, 1);

    const updatedArray = [
      ...childrenArray.slice(0, destinationIndex),
      movedItem,
      ...childrenArray.slice(destinationIndex + 1),
    ];

    const placeholderProps = calculatePlaceholderPosition(
      draggedDOM,
      updatedArray,
      destinationIndex,
    );
    setPlaceholderProps((prev) => ({
      ...prev,
      ...placeholderProps,
      // Don't override the clientHeight on updates
      clientHeight: prev?.clientHeight ?? 0,
    }));
  });

  /**
   * Handle the drag end event.
   * This is used to reorder the items.
   * This will execute the onDragEnd callback with the new ordered items.
   * It's made in this way to make it controlled from the outside since the
   * items also come from the outside.
   */
  const handleOnDragEnd = useEventCallback((result: DropResult) => {
    const { source, destination } = result;

    if (!destination || items[destination.index].isDragDisabled) {
      return items;
    }

    // Check if the destination is before a non-draggable item
    const isBeforeNonDraggable = items
      .slice(destination.index)
      .some((item) => item.isDragDisabled);

    // If dropping before a non-draggable item, don't allow the drop
    if (isBeforeNonDraggable) {
      return items;
    }

    const orderedItems = Array.from(items);
    const [reorderedItem] = orderedItems.splice(source.index, 1);

    orderedItems.splice(destination.index, 0, reorderedItem);

    return onDragEnd ? onDragEnd(orderedItems, result) : undefined;
  });

  return (
    <DragDropContext
      onDragStart={handleDragStart}
      onDragUpdate={handleDragUpdate}
      onDragEnd={handleOnDragEnd}
    >
      <Droppable droppableId='droppable' isDropDisabled={isDisabled}>
        {(provided, snapshot) => {
          const dragAndDropItemTokens = applyTokens(dragAndDropTokens.item);
          return (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{ position: 'relative', padding: 0 }}
              data-testid='droppable'
            >
              {items.map((item, index) => (
                <Draggable
                  isDragDisabled={item.isDragDisabled}
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                >
                  {(provided, snapshot) => {
                    const draggableStyles = provided.draggableProps.style as
                      | (DraggingStyle & NotDraggingStyle)
                      | undefined;

                    const { width, height, ...draggingStyles } = {
                      width: 'auto',
                      height: 'auto',
                      border: `1px solid #949494`,
                      boxShadow:
                        dragAndDropTokens.item.dragging.boxShadow.value
                          .stringValue,
                      borderRadius: '4px',
                      ':focus': {
                        boxShadow: 'none',
                      },
                    };
                    const notDraggingStyles = {
                      width: draggableStyles?.width,
                      height: draggableStyles?.height,
                      border: `1px solid transparent`,
                      boxShadow: 'none',
                      borderRadius: index === 0 ? '4px 4px 0 0' : '0',
                      ':focus': {
                        boxShadow: '0 0 0 2px #3e95fa',
                      },
                    };

                    // Prevent non-draggable items from moving during drag
                    const preventMovementStyle = item.isDragDisabled
                      ? { transform: 'none !important' }
                      : {};

                    return (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          ...dragAndDropItemTokens,
                          // NOTE: This color is not in Atlas theme
                          outline: 0,
                          userSelect: 'none',
                          alignItems: 'center',
                          // NOTE: Must be same as box shadow
                          marginBottom: '2px',
                          ...(snapshot.isDragging
                            ? draggingStyles
                            : notDraggingStyles),
                        }}
                        style={{
                          ...provided.draggableProps.style,
                          ...preventMovementStyle,
                          width,
                          height,
                        }}
                        data-qa-anchor={
                          item.isDragDisabled
                            ? 'non-draggable-item'
                            : 'draggable-item'
                        }
                        data-testid={
                          item.isDragDisabled
                            ? 'non-draggable-item'
                            : 'draggable-item'
                        }
                      >
                        {item.content}
                      </Box>
                    );
                  }}
                </Draggable>
              ))}

              {provided.placeholder}

              {!isEmpty(placeholderProps) && snapshot.isDraggingOver && (
                <Box
                  sx={{
                    ...applyTokens(dragAndDropTokens.placeholder),
                    position: 'absolute',
                    borderRadius: 0,
                    // NOTE: This color is not in Atlas theme
                    border: `1px dashed #949494`,
                  }}
                  style={{
                    top: placeholderProps.clientY,
                    left: placeholderProps.clientX,
                    right: 0,
                    height: placeholderProps.clientHeight,
                  }}
                />
              )}
            </Box>
          );
        }}
      </Droppable>
    </DragDropContext>
  );
}
